"use client";

import { cn } from "@jesus-central-church/shared";
import { motion } from "motion/react";
import { ChevronRight, Clock, MapPin } from "lucide-react";
import { Link } from "../link";
import type { EventUI } from "./types";
import { formatEventDate, getEventTypeLabel } from "./types";

export interface EventCardWideProperties {
  event: EventUI;
  variant?: "default" | "featured";
  className?: string;
}

export function EventCardWide({
  event,
  variant = "default",
  className,
}: EventCardWideProperties) {
  const { day, month, time, dayName } = formatEventDate(event.startDateTime);
  const typeLabel = event.typeLabel || getEventTypeLabel(event.type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ x: 8 }}
      className={cn("group", className)}
    >
      <Link
        href={`/${event.slug}`}
        className={cn(
          "relative flex gap-4 rounded-xl border border-freedom/10 bg-freedom/5 p-4 transition-all hover:border-strategy-gold/50 hover:bg-freedom/10 md:gap-6 md:p-6",
          variant === "featured" &&
          "border-strategy-gold/30 bg-strategy-gold/5",
        )}
      >
        {/* Accent Line */}
        <motion.div
          className="bg-strategy-gold absolute bottom-0 left-0 top-0 w-1 rounded-l-xl"
          initial={{ scaleY: 0 }}
          whileHover={{ scaleY: 1 }}
          transition={{ duration: 0.2 }}
        />

        {/* Date Block */}
        <div className="flex flex-col items-center justify-center rounded-lg bg-boldness px-4 py-3 text-center">
          <span className="text-strategy-gold text-xs font-bold uppercase">
            {dayName}
          </span>
          <span className="font-extrabold text-3xl text-freedom">{day}</span>
          <span className="text-xs text-freedom/60">{month}</span>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col justify-center gap-2">
          {/* Type Tag */}
          {typeLabel && (
            <span className="text-strategy-gold text-xs font-medium uppercase tracking-wider">
              {typeLabel}
            </span>
          )}

          {/* Title */}
          <h3 className="font-bold text-xl text-freedom transition-colors group-hover:text-strategy-gold lg:text-2xl">
            {event.title}
          </h3>

          {/* Excerpt */}
          {event.excerpt && (
            <p className="line-clamp-1 text-sm text-freedom/60">
              {event.excerpt}
            </p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-freedom/50">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {time}
            </span>
            {event.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {event.location}
              </span>
            )}
            {event.speaker && (
              <span className="text-freedom/40">Spreker: {event.speaker}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="hidden items-center gap-2 md:flex">
          {event.weeztixShopUrl && (
            <span className="bg-strategy-gold rounded-full px-4 py-2 text-sm font-medium text-freedom">
              Aanmelden
            </span>
          )}
          <ChevronRight className="h-5 w-5 text-freedom/30 transition-all group-hover:translate-x-1 group-hover:text-strategy-gold" />
        </div>
      </Link>
    </motion.div>
  );
}
